/**
 * Normalized paginated list response used across list endpoints.
 * Backend may return Ninja shape ({ items, count }) or custom ({ results, total });
 * we normalize to this shape everywhere.
 */
export interface PaginatedResponse<T> {
  results: T[];
  total: number;
}

/**
 * Raw paginated response from API (Ninja uses items/count; network devices use results/total).
 */
type RawPaginatedPayload<T> = {
  items?: T[];
  results?: T[];
  count?: number;
  total?: number;
};

/**
 * Normalize API pagination response to { results, total }.
 * Handles both Django Ninja (@paginate: items/count) and custom (results/total) responses.
 */
export function normalizePaginatedResponse<T>(
  data: RawPaginatedPayload<T> | T[] | null | undefined
): PaginatedResponse<T> {
  if (!data) {
    return { results: [], total: 0 };
  }
  if (Array.isArray(data)) {
    return { results: data, total: data.length };
  }
  if (!data.results && !data.items && data.count == null && data.total == null) {
    if (import.meta.env.DEV) {
      console.warn('[normalizePaginatedResponse] Response has no pagination fields:', Object.keys(data));
    }
  }
  const results = data.results ?? data.items ?? [];
  const total = data.total ?? data.count ?? results.length;
  return { results: Array.isArray(results) ? results : [], total: Number(total) };
}

/**
 * Query-cache helpers for `setQueryData` on a list-cache entry.
 *
 * A list endpoint's cache entry only ever holds what its own `queryFn`
 * produced — for `getFloors()`/`getDoors()`/`getAddresses()`/`getTenants()`
 * that is a `PaginatedResponse<T>` (`{results, total}`), never a bare `T[]`.
 * Patching such an entry with inline `old?.map`/`[...old, x]` threw
 * ("old.map is not a function") inside a mutation's `onSuccess`, surfacing as
 * a false error toast after a save that had in fact succeeded.
 *
 * These helpers patch the `results` of a paginated entry (keeping any other
 * fields and adjusting `total`), still accept a bare array for list caches
 * whose fetcher unwraps `results`, and leave a missing entry missing (the
 * follow-up invalidation fetches it fresh).
 */
export type ListCache<T> = PaginatedResponse<T> | T[] | undefined;

export function mapPaginated<T>(old: PaginatedResponse<T> | undefined, fn: (item: T) => T): PaginatedResponse<T> | undefined;
export function mapPaginated<T>(old: T[] | undefined, fn: (item: T) => T): T[] | undefined;
export function mapPaginated<T>(old: ListCache<T>, fn: (item: T) => T): ListCache<T> {
  if (!old) return old;
  if (Array.isArray(old)) return old.map(fn);
  return { ...old, results: old.results.map(fn) };
}

export function filterPaginated<T>(old: PaginatedResponse<T> | undefined, predicate: (item: T) => boolean): PaginatedResponse<T> | undefined;
export function filterPaginated<T>(old: T[] | undefined, predicate: (item: T) => boolean): T[] | undefined;
export function filterPaginated<T>(old: ListCache<T>, predicate: (item: T) => boolean): ListCache<T> {
  if (!old) return old;
  if (Array.isArray(old)) return old.filter(predicate);
  const results = old.results.filter(predicate);
  return { ...old, results, total: Math.max(0, old.total - (old.results.length - results.length)) };
}

export function appendPaginated<T>(old: PaginatedResponse<T> | undefined, item: T): PaginatedResponse<T> | undefined;
export function appendPaginated<T>(old: T[] | undefined, item: T): T[] | undefined;
export function appendPaginated<T>(old: ListCache<T>, item: T): ListCache<T> {
  if (!old) return old;
  if (Array.isArray(old)) return [...old, item];
  return { ...old, results: [...old.results, item], total: old.total + 1 };
}

/** The items of a list-cache entry, whichever shape it holds (`[]` if missing). */
export function listItems<T>(old: ListCache<T>): T[] {
  if (!old) return [];
  return Array.isArray(old) ? old : old.results;
}
